import { Box, Skeleton, Modal, Typography } from '@mui/material'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import iconWearable from '../../assets/dashboard/device.svg'
import iconCalendar from '../../assets/dashboard/calendar.svg'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getContentContainer } from '../../container/statistics-module'
import { IWearableService, WEARABLE_SERVICE_KEY } from '../../modules/statistics'
import { Query } from '../../common/api/Query'
import { emptyList, ItemList } from '../../common/models/ItemList'
import { Wearable } from '../../modules/statistics/models/Wearable'
import { getUserContainer } from '../../container/user-module'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import {
  emptyWearableDTO,
  fromModel,
  WearableDTO,
} from '../../modules/statistics/models/WearableDTO'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import modalStyles from '../../components/modal/CustomModal.module.css'
import styles from './WearableAssignment.module.css'

type WearableAssignmentProps = {
  userId: string
}

const wearableService = getContentContainer().get<IWearableService>(WEARABLE_SERVICE_KEY)
const userContainer = getUserContainer()
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

const isValidMac = (mac: string): boolean => {
  const macRegex = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/
  return macRegex.test(mac)
}

export const WearableAssignment = (props: WearableAssignmentProps) => {
  const { t } = useTranslation()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [wearableList, setWearableList] = useState<ItemList<Wearable>>(emptyList())
  const [wearableDTO, setWearableDTO] = useState<WearableDTO>(emptyWearableDTO())
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [macError, setMacError] = useState<string>('')

  const loggedUser = loggedUserService.get()
  const { canLinkWearable } = useUserRoles(loggedUser)

  useEffect(() => {
    if (!props.userId) return
    wearableService
      .getFilteredList(
        new Query({
          query: [
            {
              name: 'userId',
              value: props.userId,
            },
          ],
        })
      )
      .subscribe((res) => {
        if (!res) return
        setWearableList(res)
        setIsLoading(false)
      })
  }, [isLoading, openModal, props.userId])

  useEffect(() => {
    if (!wearableList.items[0]?.id) return
    wearableService.getByID(wearableList.items[0].id).subscribe((res) => {
      if (!res) return
      setWearableDTO(fromModel(res))
    })
  }, [wearableList])

  const handleCloseModal = () => {
    setOpenModal(false)
    setIsLoading(true)
    setMacError('')
  }

  const handleChangeMac = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!canLinkWearable) return
    setWearableDTO({ ...wearableDTO, [e.target.name]: e.target.value })
    setMacError('')
  }

  const handleAssign = () => {
    if (!canLinkWearable) return
    if (!isValidMac(wearableDTO.mac)) {
      setMacError(t('invalidMacFormat'))
      return
    }

    const newWearable: WearableDTO = {
      id: wearableDTO.id,
      idUser: props.userId,
      mac: wearableDTO.mac,
      date: new Date(),
    }

    if (wearableList.items[0]?.mac) {
      wearableService.update(newWearable).subscribe(() => handleCloseModal())
    } else {
      wearableService.add(newWearable).subscribe(() => handleCloseModal())
    }
  }

  const handleDelete = () => {
    if (!canLinkWearable) return
    wearableService.delete(wearableDTO.id).subscribe(() => handleCloseModal())
  }

  const macValue = wearableList?.items[0]?.mac || t('notAssigned')

  return (
    <>
      <Box className={styles.containerBoxLittle}>
        <Box className={styles.titleBox}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            <p className={styles.titleText}>
              {isLoading ? <Skeleton width={'10rem'} /> : t('wearables')}
            </p>
            <Box style={{ marginTop: 8 }}>
              <p className={styles.text}>
                {isLoading ? <Skeleton width={'8rem'} /> : `MAC: ${macValue}`}
              </p>
            </Box>
          </Box>

          <img
            className={styles.imageBox}
            src={iconWearable}
            alt={t('wearableIcon')}
            width={80}
            height={80}
          />
        </Box>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Box style={{ marginBottom: 8 }}>
            {isLoading ? (
              <Skeleton width={100} height={36} />
            ) : (
              <>
                {canLinkWearable && (
                  <AppButton
                    label={t('assign')}
                    theme={ButtonTheme.NewPrimary}
                    handler={() => setOpenModal(true)}
                    type={'button'}
                  />
                )}
              </>
            )}
          </Box>
          <Box className={styles.updateBox}>
            <p className={styles.titleUpdateBox}>{t('updated')}</p>
            <img
              className={styles.imageCalendar}
              src={iconCalendar}
              alt={t('calendarIcon')}
              width={20}
              height={20}
            />
          </Box>
        </Box>
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="wearable-modal-title"
        aria-describedby="wearable-modal-description"
      >
        <Box className={modalStyles.agoraContainer}>
          <Box className={styles.containerModal}>
            <Box
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '16px',
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                className={styles.label}
                style={{ minWidth: 'fit-content', marginTop: '10px' }}
              >
                {t('MAC')}
              </Typography>

              <Box style={{ flex: 1, minWidth: '200px' }}>
                <Input
                  value={wearableDTO.mac}
                  id="mac"
                  name="mac"
                  label={''}
                  variant="outlined"
                  required={true}
                  fullWidth={true}
                  disabled={!canLinkWearable}
                  onChange={handleChangeMac}
                  error={!!macError}
                  helperText={macError}
                />
              </Box>

              <Box
                style={{
                  display: 'flex',
                  gap: '8px',
                  marginLeft: 'auto',
                  marginTop: '8px',
                }}
              >
                {canLinkWearable ? (
                  <>
                    <AppButton
                      label={t('assign')}
                      theme={ButtonTheme.NewPrimary}
                      handler={handleAssign}
                      type={'button'}
                    />
                    {wearableDTO.mac.length > 0 && (
                      <AppButton
                        label={t('delete')}
                        theme={ButtonTheme.NewPrimary}
                        handler={handleDelete}
                        type={'button'}
                      />
                    )}
                  </>
                ) : (
                  <AppButton
                    label={t('close')}
                    theme={ButtonTheme.NewPrimary}
                    handler={handleCloseModal}
                    type={'button'}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
